import { Box, BoxProps } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { ReactElement, useEffect, useRef, useState } from 'react';

import { useMultiStyleConfig } from '../../system/hooks';
import { ReactElementWithDisplayName } from '../../types';

import { useSideBarContext } from './SideBarContext';
import { SideBarItemProps } from './SideBarItem';
import { ToggleActiveNavItem } from './effects';
import DisplayNames from './enums';

export type SideBarItemGroupProps = SideBarItemProps;

const SideBarItemGroup = (props: SideBarItemGroupProps): ReactElement => {
  const { children, isActive, onClick, ...rest } = props;

  const sideBarItemGroupEl = useRef();
  const [leftElExists, setLeftElExists] = useState(false);
  const [rightElExists, setRightElExists] = useState(false);
  const { isCurrentActiveItem, setCurrentActiveItem } = useSideBarContext();

  const isCurrentlyActive = isCurrentActiveItem(sideBarItemGroupEl);
  const { sideBarItemGroup: themeStyles } = useMultiStyleConfig('SideBar', {
    ...props,
    isActive: isCurrentlyActive
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(ToggleActiveNavItem(sideBarItemGroupEl, isActive), [isActive]);

  const childArray = React.Children.toArray(children) as ReactElementWithDisplayName[];

  const getHOCChild = (displayName: DisplayNames) => {
    return (childArray.find(child => child.type.displayName === displayName) as unknown) as React.ReactElement;
  };
  const getLeftEl = () => {
    const leftEl = getHOCChild(DisplayNames.SideBarItemLeftElement);

    if (leftEl && !leftElExists) setLeftElExists(true);

    return leftEl && React.cloneElement(leftEl, { ...leftEl.props, isActive: isCurrentlyActive });
  };
  const getRightEl = () => {
    const rightEl = getHOCChild(DisplayNames.SideBarItemRightElement);
    if (rightEl && !rightElExists) setRightElExists(true);

    return rightEl && React.cloneElement(rightEl, { ...rightEl.props, isActive: isCurrentlyActive });
  };
  const getMiddleEl = () => {
    const middleEl = getHOCChild(DisplayNames.SideBarItem);
    const alteredProps = {
      tabIndex: null,
      _hover: null,
      _active: null,
      _focus: null,
      _focusVisible: null,
      'aria-label': null,
      as: 'span',
      width: null,
      height: null,
      padding: null,
      margin: 0,
      color: 'inherit',
      paddingLeft: leftElExists ? '8px' : 0,
      paddingRight: rightElExists ? '8px' : 0
    };

    return middleEl && React.cloneElement(middleEl, { ...middleEl.props, ...alteredProps });
  };

  const handleOnClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setCurrentActiveItem(sideBarItemGroupEl);

    if (onClick) {
      onClick(evt);
    }
  };

  const computedProps = {
    ...(themeStyles as BoxProps),
    'aria-label': 'sidebar item',
    as: 'button',
    tabIndex: 0,
    onClick: handleOnClick,
    ref: sideBarItemGroupEl,
    ...rest
  } as BoxProps;

  return (
    <Box data-testid="sidebar-item-group" type="button" {...computedProps}>
      {getLeftEl()}
      {getMiddleEl()}
      {getRightEl()}
    </Box>
  );
};

SideBarItemGroup.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool
};
SideBarItemGroup.defaultProps = {
  isActive: false
};
SideBarItemGroup.displayName = DisplayNames.SideBarItemGroup;

export default React.memo(SideBarItemGroup);
